if (!customElements.get('fade-button-tabs')) {
  class FadeButtonTabs extends HTMLElement {
    connectedCallback() {
      this._activeIdx  = 0;
      this._animating  = false;
      this._slides     = Array.from(this.querySelectorAll('.fbt__slide'));
      this._tabs       = Array.from(this.querySelectorAll('.fbt__tab-btn'));
      this._descs      = Array.from(this.querySelectorAll('.fbt__mobile-desc'));
      this._prevBtn    = this.querySelector('.fbt__prev');
      this._nextBtn    = this.querySelector('.fbt__next');

      // Position all non-active slides off-screen immediately (no transition)
      this._slides.forEach((slide, i) => {
        if (i !== 0) this._setTransform(slide, 1, true);
      });

      // Desktop tab clicks
      this._tabs.forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.index, 10);
          if (idx !== this._activeIdx && !this._animating) this._switch(idx);
        });
      });

      // Mobile arrows
      this._prevBtn?.addEventListener('click', () => {
        if (this._animating) return;
        const idx = (this._activeIdx - 1 + this._slides.length) % this._slides.length;
        this._switch(idx);
      });

      this._nextBtn?.addEventListener('click', () => {
        if (this._animating) return;
        const idx = (this._activeIdx + 1) % this._slides.length;
        this._switch(idx);
      });

      // Fade-in on scroll
      if (this.classList.contains('fbt--fade')) this._initFade();
    }

    /* ── helpers ────────────────────────────────────────────────────────── */

    _isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    /**
     * Set a slide's transform.
     * dir: -1 = above/left, 0 = center, 1 = below/right
     * instant: skip transition
     */
    _setTransform(slide, dir, instant = false) {
      const axis   = this._isMobile() ? 'translateX' : 'translateY';
      const value  = dir === 0 ? '0%' : `${dir * 100}%`;
      if (instant) slide.style.transition = 'none';
      slide.style.transform = `${axis}(${value})`;
    }

    _enableTransition(slide) {
      const dur = this._isMobile() ? '0.42s' : '0.52s';
      slide.style.transition = `transform ${dur} cubic-bezier(0.4, 0, 0.2, 1)`;
    }

    /* ── switch ──────────────────────────────────────────────────────────── */

    _switch(newIdx) {
      const oldIdx   = this._activeIdx;
      const dir      = newIdx > oldIdx ? 1 : -1; // 1 = forward/down, -1 = back/up
      this._animating = true;

      const oldSlide = this._slides[oldIdx];
      const newSlide = this._slides[newIdx];

      // Snap new slide to starting off-screen position (no animation yet)
      this._setTransform(newSlide, dir, true);

      // Double rAF ensures the browser renders the snap before the animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._enableTransition(oldSlide);
          this._enableTransition(newSlide);

          this._setTransform(newSlide, 0);   // slide in to center
          this._setTransform(oldSlide, -dir); // slide out opposite direction

          newSlide.classList.add('is-active');
        });
      });

      // Update UI immediately (no need to wait for animation)
      this._tabs.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === newIdx);
        btn.setAttribute('aria-selected', String(i === newIdx));
      });
      this._descs.forEach((desc, i) => {
        desc.classList.toggle('is-active', i === newIdx);
      });

      // Cleanup after transition
      const duration = this._isMobile() ? 440 : 540;
      setTimeout(() => {
        oldSlide.classList.remove('is-active');
        oldSlide.style.transition = '';
        newSlide.style.transition = '';
        this._activeIdx  = newIdx;
        this._animating  = false;
      }, duration);
    }

    /* ── fade-in ─────────────────────────────────────────────────────────── */

    _initFade() {
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => this.classList.toggle('is-visible', e.isIntersecting));
      }, { threshold: 0.12 });
      io.observe(this);
    }
  }

  customElements.define('fade-button-tabs', FadeButtonTabs);
}
